import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Join() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ code: "", name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const c = params.get("code");
    if (c) setF((s) => ({ ...s, code: c.toUpperCase() }));
  }, [params]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/join", { ...f, code: f.code.trim().toUpperCase() });
      login(data);
      toast.success(`Joined as ${data.user.role}`);
      navigate("/console");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080B11] flex items-center justify-center p-6 relative">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-md bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-cyan-400" />
          </div>
          <span className="font-display font-black text-xl tracking-tight">Join Workspace</span>
        </div>
        <div className="hud-card accent-top rounded-xl p-8" style={{ "--accent-line": "#06B6D4" }}>
          <p className="text-sm text-slate-400 mb-5">Enter your invite code and create your operator account.</p>
          <form onSubmit={submit} className="space-y-4">
            {[["Invite Code", "code", "text"], ["Full Name", "name", "text"], ["Email", "email", "email"], ["Password", "password", "password"]].map(([label, key, type]) => (
              <div key={key}>
                <label className="font-mono text-[10px] font-semibold tracking-[0.2em] uppercase text-slate-400 mb-1.5 block">{label}</label>
                <input data-testid={`join-${key}-input`} type={type} value={f[key]} onChange={set(key)} required
                  className="w-full px-3 py-2.5 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500/60 uppercase-none" />
              </div>
            ))}
            {error && <div data-testid="join-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">{error}</div>}
            <button type="submit" data-testid="join-submit-btn" disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-md bg-cyan-500 text-[#080B11] font-bold hover:bg-cyan-400 transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Join Workspace <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
          <div className="mt-5 text-center text-[11px] font-mono text-slate-500">
            Have an account? <a href="/login" className="text-cyan-400">Sign in</a>
          </div>
        </div>
      </div>
    </div>
  );
}
